import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import Editor from '@monaco-editor/react';
import { useEffect, useState, useRef } from 'react';
import { Event } from '../services/monitor-api.service';
import { monitorApi } from '../services/monitor-api.service';
import { Action, canPerformAction } from '../services/permissions.service';
import { useAuth } from '../contexts/AuthContext';

interface EventDetailsDialogProps {
    eventId: number | null;
    open: boolean;
    onClose: () => void;
    onRetry: (eventId: number) => void;
    onMarkDead: (eventId: number) => void;
    onMarkHandled: (eventId: number) => void;
    onMarkWarning: (eventId: number) => void;
    onBodyUpdate?: (eventId: number, body: string) => Promise<void>;
    retryingEventId: number | null;
    markingDeadEventId: number | null;
    markingHandledEventId: number | null;
    markingWarningEventId: number | null;
}

export const getStatusColor = (status: string) => {
    switch (status) {
        case 'new':
            return 'default';
        case 'success':
            return 'success';
        case 'warning':
            return 'info';
        case 'retry':
            return 'warning';
        case 'dead':
            return 'default';
        case 'handled':
            return 'success'; // Use success as base, but getStatusSx overrides with light green
        case 'disabled':
            return 'secondary';
        default:
            return 'default';
    }
};

export const getStatusSx = (status: string) => {
    if (status === 'handled') {
        return {
            backgroundColor: '#a5d6a7', // Light green
            color: '#1b5e20', // Dark green text
        };
    }
    return {};
};

export function EventDetailsDialog({
    eventId,
    open,
    onClose,
    onRetry,
    onMarkDead,
    onMarkHandled,
    onMarkWarning,
    onBodyUpdate,
    retryingEventId,
    markingDeadEventId,
    markingHandledEventId,
    markingWarningEventId,
}: EventDetailsDialogProps) {
    const [fullEvent, setFullEvent] = useState<Event | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editorValue, setEditorValue] = useState<string>('');
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    const originalBodyRef = useRef<string>('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { role } = useAuth();
    const canRetry = canPerformAction(role, Action.retryEvents);
    const canEdit = canPerformAction(role, Action.retryEvents); // Use same permission as retry

    useEffect(() => {
        if (open && eventId) {
            fetchEventDetails(eventId);
        } else if (!open) {
            // Reset state when dialog closes
            setFullEvent(null);
            setError(null);
            setEditorValue('');
            setHasChanges(false);
            originalBodyRef.current = '';
        }
    }, [open, eventId]);

    useEffect(() => {
        if (fullEvent?.body) {
            const formattedBody = formatJsonBody(fullEvent.body);
            setEditorValue(formattedBody);
            originalBodyRef.current = formattedBody;
            setHasChanges(false);
        }
    }, [fullEvent]);

    const fetchEventDetails = async (eventId: number) => {
        try {
            setLoading(true);
            setError(null);
            const response = await monitorApi.getEvent(eventId);
            setFullEvent(response.event);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch event details');
        } finally {
            setLoading(false);
        }
    };

    const formatJsonBody = (body: string | undefined) => {
        if (!body) return 'No body data';
        try {
            const parsed = JSON.parse(body);
            return JSON.stringify(parsed, null, 2);
        } catch {
            return body; // Return as-is if not valid JSON
        }
    };

    const copyToClipboard = async () => {
        if (fullEvent?.body) {
            try {
                await navigator.clipboard.writeText(formatJsonBody(fullEvent.body));
            } catch (err) {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = formatJsonBody(fullEvent.body);
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }
        }
    };

    const handleEditorChange = (value: string | undefined) => {
        const newValue = value || '';
        setEditorValue(newValue);
        setHasChanges(newValue !== originalBodyRef.current);
    };

    const handleSave = async () => {
        if (!eventId || !onBodyUpdate || !hasChanges) return;

        // Validate JSON before saving
        try {
            JSON.parse(editorValue);
        } catch {
            setError('Invalid JSON. Please fix syntax errors before saving.');
            return;
        }

        try {
            setSaving(true);
            setError(null);
            await onBodyUpdate(eventId, editorValue);
            originalBodyRef.current = editorValue;
            setHasChanges(false);
            // Refresh the event to get the updated data
            await fetchEventDetails(eventId);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    const displayEvent = fullEvent;
    const handleRetryAndClose = () => {
        if (eventId) {
            onRetry(eventId);
            onClose();
        }
    };

    const handleMarkDeadAndClose = () => {
        if (eventId) {
            onMarkDead(eventId);
            onClose();
        }
    };

    const handleMarkHandledAndClose = () => {
        if (eventId) {
            onMarkHandled(eventId);
            onClose();
        }
    };

    const handleMarkWarningAndClose = () => {
        if (eventId) {
            onMarkWarning(eventId);
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={isMobile ? false : 'md'}
            fullWidth={!isMobile}
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    ...(isMobile && {
                        m: 0,
                        width: '100%',
                        height: '100%',
                        maxHeight: '100%',
                        borderRadius: 0,
                    }),
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: isMobile ? 2 : 3,
                    py: isMobile ? 1.5 : 2,
                }}
            >
                <Typography variant={isMobile ? 'h6' : 'h6'}>Event Details - ID: {eventId}</Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: isMobile ? 2 : 3, py: isMobile ? 1 : 2 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ ml: 2 }}>
                            Loading event details...
                        </Typography>
                    </Box>
                )}

                {error && (
                    <Box sx={{ mb: 2 }}>
                        <Paper
                            sx={{ p: 2, backgroundColor: 'error.50', border: '1px solid', borderColor: 'error.200' }}
                        >
                            <Typography color="error">{error}</Typography>
                        </Paper>
                    </Box>
                )}

                {displayEvent && !loading && (
                    <Box sx={{ '& > *': { mb: isMobile ? 2 : 1.5 } }}>
                        {/* Target Function with Retry Button */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                {displayEvent.functionName.replace('handle', '')}
                            </Typography>
                            {displayEvent && canRetry && (
                                <IconButton
                                    onClick={handleRetryAndClose}
                                    color="warning"
                                    disabled={retryingEventId === displayEvent.id}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                    title="Resend"
                                >
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            )}
                            <Box sx={{ flexGrow: 1 }} />
                            {displayEvent &&
                                canRetry &&
                                (displayEvent.status === 'retry' ||
                                    displayEvent.status === 'warning' ||
                                    displayEvent.status === 'handled' ||
                                    displayEvent.status === 'dead' ||
                                    displayEvent.status === 'disabled') && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mr: { xs: 0, sm: 0.5 }, width: { xs: '100%', sm: 'auto' } }}
                                        >
                                            Mark as:
                                        </Typography>
                                        {(displayEvent.status === 'retry' ||
                                            displayEvent.status === 'handled' ||
                                            displayEvent.status === 'dead' ||
                                            displayEvent.status === 'disabled') && (
                                            <Button
                                                onClick={handleMarkWarningAndClose}
                                                color="info"
                                                variant="outlined"
                                                size="small"
                                                disabled={markingWarningEventId === displayEvent.id}
                                            >
                                                Warning
                                            </Button>
                                        )}
                                        {(displayEvent.status === 'retry' ||
                                            displayEvent.status === 'warning' ||
                                            displayEvent.status === 'dead' ||
                                            displayEvent.status === 'disabled') && (
                                            <Button
                                                onClick={handleMarkHandledAndClose}
                                                color="success"
                                                variant="outlined"
                                                size="small"
                                                disabled={markingHandledEventId === displayEvent.id}
                                            >
                                                Handled
                                            </Button>
                                        )}
                                        {(displayEvent.status === 'retry' ||
                                            displayEvent.status === 'warning' ||
                                            displayEvent.status === 'handled' ||
                                            displayEvent.status === 'disabled') && (
                                            <Button
                                                onClick={handleMarkDeadAndClose}
                                                color="error"
                                                variant="outlined"
                                                size="small"
                                                disabled={markingDeadEventId === displayEvent.id}
                                            >
                                                Dead
                                            </Button>
                                        )}
                                    </Box>
                                )}
                        </Box>

                        {/* Description */}
                        <Box>
                            <Typography variant="body1">{displayEvent.description}</Typography>
                        </Box>

                        {/* Status */}
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Status:
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Chip
                                        label={displayEvent.status}
                                        color={getStatusColor(displayEvent.status) as any}
                                        size="small"
                                        sx={{ marginLeft: '4px', ...getStatusSx(displayEvent.status) }}
                                    />
                                    <Chip
                                        label={`${displayEvent.retryCount} retries`}
                                        size="small"
                                        variant="outlined"
                                    />
                                    {displayEvent.hasTimedOut && (
                                        <Chip label="Timed Out" color="error" size="small" variant="outlined" />
                                    )}
                                </Box>
                                {(displayEvent.outcome || displayEvent.lastError) && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                        {displayEvent.outcome && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    color:
                                                        displayEvent.status === 'warning'
                                                            ? 'info.main'
                                                            : 'text.primary',
                                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                }}
                                            >
                                                &nbsp;Outcome: {displayEvent.outcome}
                                            </Typography>
                                        )}
                                        {displayEvent.lastError && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    color: 'error.main',
                                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                }}
                                            >
                                                &nbsp;Last error: {displayEvent.lastError}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* JSON Body Section */}
                        {fullEvent?.body && (
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    Event Data:
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Chip
                                            label={displayEvent.method}
                                            variant="outlined"
                                            size="small"
                                            sx={{ marginLeft: '4px' }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: 'monospace',
                                                wordBreak: 'break-all',
                                                fontSize: isMobile ? '0.75rem' : '0.875rem',
                                            }}
                                        >
                                            {displayEvent.path}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {canEdit && hasChanges && (
                                            <IconButton
                                                size="small"
                                                onClick={handleSave}
                                                disabled={saving}
                                                color="primary"
                                                sx={{ p: 0.5 }}
                                                title="Save changes"
                                            >
                                                {saving ? (
                                                    <CircularProgress size={18} />
                                                ) : (
                                                    <SaveIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        )}
                                        <IconButton size="small" onClick={copyToClipboard} sx={{ p: 0.5 }}>
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        border: '1px solid',
                                        borderColor: hasChanges ? 'primary.main' : 'grey.200',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        marginLeft: '4px',
                                    }}
                                >
                                    <Editor
                                        height={isMobile ? '400px' : '350px'}
                                        language="json"
                                        value={editorValue}
                                        onChange={handleEditorChange}
                                        options={{
                                            minimap: { enabled: false },
                                            fontSize: isMobile ? 12 : 14,
                                            lineNumbers: 'off',
                                            folding: true,
                                            wordWrap: 'on',
                                            scrollBeyondLastLine: false,
                                            automaticLayout: true,
                                            readOnly: !canEdit,
                                            tabSize: 2,
                                            formatOnPaste: true,
                                            formatOnType: true,
                                        }}
                                        theme="vs-dark"
                                    />
                                </Box>
                                {hasChanges && (
                                    <Typography
                                        variant="caption"
                                        color="primary"
                                        sx={{ mt: 0.5, display: 'block', marginLeft: '4px' }}
                                    >
                                        Unsaved changes - click the save button to update
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: isMobile ? 2 : 3, py: isMobile ? 1.5 : 1 }}>
                {hasChanges && (
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        variant="contained"
                        color="primary"
                        startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                        sx={{ mr: 'auto' }}
                    >
                        Save Changes
                    </Button>
                )}
                <Button onClick={onClose} fullWidth={isMobile && !hasChanges}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
